/**
 * Bar arrangement.
 *
 * Two ways to look at the same six strings, because guitarists do both:
 * stacked reads like tablature, columns read like the instrument seen head-on.
 */

import type { GuitarString } from '../../music/tunings'

/** Available arrangements. */
export type BarLayout = 'stacked' | 'columns'

/** Default arrangement: tablature order, high E on top. */
export const DEFAULT_LAYOUT: BarLayout = 'stacked'

/** localStorage key holding the preferred arrangement. */
export const LAYOUT_STORAGE_KEY = 'multitimbral.barLayout'

/** Human-readable names for the layout switch. */
export const LAYOUT_LABELS: Record<BarLayout, string> = {
  stacked: 'Stacked (high E on top)',
  columns: 'Columns (low E on the left)',
}

/** Narrow an arbitrary string to a known layout. */
export function toLayout(value: string | null): BarLayout {
  return value === 'columns' || value === 'stacked' ? value : DEFAULT_LAYOUT
}

/**
 * Order the strings for display.
 *
 * Tunings list strings low to high. `columns` keeps that order so the lowest
 * string sits on the left, matching the instrument seen from the front.
 * `stacked` reverses it so the highest string is the top row, matching how
 * tablature is written.
 */
export function orderStringsFor(
  layout: BarLayout,
  strings: readonly GuitarString[],
): readonly GuitarString[] {
  return layout === 'stacked' ? [...strings].reverse() : [...strings]
}
